import React, { useEffect, useRef, useState } from 'react';

import RadarChart from './graph_components/RadarChart';
import WaterHistogramChart from './graph_components/WaterHistogramChart';
import DecisionCategoryChart from './graph_components/DecisionCategoryChart';
import AdvanceButton from '../other_components/AdvanceButton';
import ReportDownloadButton from './ReportDownloadButton';
import {
  CATEGORY_COLORS,
  CATEGORY_LABELS,
  NEUTRAL_COLOR,
  SECTOR_COLORS,
  actorLedger,
  adoptedMeasures,
  biggestEffort,
  breakdownBy,
  measureConsensus,
  objectiveProgress,
  podium,
  sectorSavings,
} from './acgSummary';
import { targetPerRoundOf } from './objectiveCurve';

/**
 * LE BILAN D'UNE SÉANCE COMPTE-GOUTTES.
 *
 * Trois lectures, dans l'ordre où une salle les attend : ce que le territoire a
 * gagné, ce que la salle a choisi comme leviers, et ce que ça a coûté aux
 * acteurs. Les calculs sont dans acgSummary.js et testés — ici il n'y a que de
 * la mise en page.
 *
 * TOUT BLOC DONT LA DONNÉE MANQUE DISPARAÎT. Une partie commencée avant le
 * journal des mesures n'a ni titres ni positions d'acteurs : l'écran montre
 * alors moins de choses, il n'en invente aucune. C'est la règle qui distingue
 * ce bilan de celui de Disney, qui affiche un percentile tiré d'une loi normale
 * codée en dur et une phrase que rien ne vérifie.
 *
 * L'ÉCRAN EST PROJETÉ ET COMMENTÉ, jamais parcouru à la souris. D'où la mise en
 * page : les chiffres de la séance en haut dans un seul bandeau (c'est ce que
 * l'animateur annonce en premier), puis trois sections qui répondent chacune à
 * une question. Une couleur ne veut dire qu'une chose sur toute la page — un
 * secteur garde la teinte que lui donne l'histogramme (SECTOR_COLORS), et
 * l'orange marque partout la contrainte et l'opposition.
 *
 * Réglages lus dans `content.metadata` (tous optionnels) :
 *   selected_metrics   — les axes du radar
 *   target_per_round   — points d'empreinte visés par tour ; à défaut celui
 *                        de l'atelier (`games_config.target_per_round`), sinon 20.
 *                        La même lecture que la courbe d'objectif d'entre
 *                        deux tours (objectiveCurve.js), sinon les deux
 *                        écrans annonceraient deux objectifs.
 *   background_image_url — le décor
 */
const DEFAULT_METRICS = ['economy', 'carbon_economy', 'water_quality', 'popularity', 'biodiversity'];
const DEFAULT_BACKGROUND =
  'https://res.cloudinary.com/dc1felxem/image/upload/v1740495509/hydros90_22124_Futuristic_smart_water_meter_with_real-time_co_3b116eaa-55ba-4ded-8931-7fc969055c98_0_wdtcux.jpg';

const MEDALS = ['🥇', '🥈', '🥉'];

const percent = (share) => `${Math.round(Math.max(0, Math.min(1, share)) * 100)}%`;

/**
 * La carte, seule boîte de la page : fond blanc, coins francs, ombre portée.
 *
 * `--titled` n'est pas décoratif : c'est lui qui efface le titre que les
 * graphes partagés portent en propre. Une carte sans titre le laisse donc
 * paraître, comme sur les pages de type `score`.
 */
const Card = ({ title, hint, children, wide = false, chartKey }) => (
  <section
    className={`board-card${wide ? ' board-card--wide' : ''}${title ? ' board-card--titled' : ''}`}
    /* `data-report-chart` désigne les cartes dont le canvas part dans le
       rapport PDF (acgReport.captureCharts). Un marqueur nommé plutôt qu'un
       relevé des canvas dans l'ordre d'apparition : sinon un histogramme se
       retrouverait légendé « radar » le jour où une carte serait déplacée. */
    data-report-chart={chartKey}
  >
    {title && <h3 className="board-card-title">{title}</h3>}
    {hint && <p className="board-card-hint">{hint}</p>}
    {children}
  </section>
);

/** Une ligne de secteur : pastille, libellé, barre à sa couleur, valeur. */
const SectorRow = ({ row }) => {
  const color = SECTOR_COLORS[row.key] || NEUTRAL_COLOR;
  return (
    <div className="acg-sector-row">
      <span className="acg-sector-label">
        <span className="acg-dot" style={{ background: color }} aria-hidden="true" />
        {row.label}
      </span>
      <span className="acg-sector-track">
        <span
          className="acg-sector-fill"
          style={{ width: percent(row.share), background: color }}
        />
      </span>
      <span className="acg-sector-value">
        {Math.round(row.value)}
        <span className="acg-sector-share">{percent(row.share)}</span>
      </span>
    </div>
  );
};

/**
 * LA NATURE DES MESURES, en une seule barre partagée.
 *
 * Trois barres séparées demandaient de comparer trois longueurs pour retrouver
 * une proportion qui fait 100 % : une barre segmentée la donne d'un regard, et
 * c'est la seule chose que ce bloc a à dire.
 */
const CategorySplit = ({ rows }) => (
  <>
    <div className="acg-split-bar">
      {rows.map((row) => (
        <span
          key={row.key}
          className="acg-split-seg"
          style={{ width: percent(row.share), background: CATEGORY_COLORS[row.key] || NEUTRAL_COLOR }}
        >
          {/* En dessous d'un sixième de la barre, le pourcentage ne tient pas
              dans son segment : il reste lisible dans la légende. */}
          {row.share >= 0.16 && <span className="acg-split-seg-value">{percent(row.share)}</span>}
        </span>
      ))}
    </div>
    <ul className="acg-split-legend">
      {rows.map((row) => (
        <li key={row.key}>
          <span
            className="acg-dot"
            style={{ background: CATEGORY_COLORS[row.key] || NEUTRAL_COLOR }}
            aria-hidden="true"
          />
          <span className="acg-split-name">{row.label}</span>
          <span className="acg-split-count">
            {row.count} mesure{row.count > 1 ? 's' : ''}
          </span>
          <span className="acg-split-share">{percent(row.share)}</span>
        </li>
      ))}
    </ul>
  </>
);

/**
 * Une mesure du débrief : son titre, le rapport de force, le décompte.
 *
 * Rien ne distingue les deux colonnes dans le dessin de l'élément lui-même :
 * la colonne le dit déjà, et le rapport de force le montre.
 */
const DebateItem = ({ row }) => (
  <li className="acg-debate-item">
    <span className="acg-debate-title">{row.title}</span>
    <span className="acg-debate-meter" aria-hidden="true">
      <span className="acg-debate-for" style={{ flexGrow: row.forCount }} />
      <span className="acg-debate-against" style={{ flexGrow: row.againstCount }} />
    </span>
    <span className="acg-debate-counts">
      <span className="acg-pill acg-pill--for">{row.forCount} pour</span>
      {row.againstCount > 0 ? (
        <span className="acg-pill acg-pill--against">{row.againstCount} contre</span>
      ) : (
        <span className="acg-pill acg-pill--none">aucune opposition</span>
      )}
    </span>
  </li>
);

/**
 * LE CLASSEMENT PARMI LES AUTRES SÉANCES, demandé au serveur.
 *
 * Il est le seul élément du bilan que l'écran ne peut pas calculer : il faut
 * regarder d'autres parties que la sienne. `/game_summary` rend un percentile
 * plus l'effectif — qui est affiché, parce que c'est lui qui rend la phrase
 * vérifiable — et `basis`, QUI DIT DE QUELLES SÉANCES ON PARLE :
 *
 *   `workshop`  — les séances déjà jouées sur cet atelier, loi normale ajustée
 *                 à leurs scores réels ;
 *   `reference` — le repli quand l'atelier n'en a pas encore cinq : une
 *                 distribution figée de 228 séances (cf. game_summary.py).
 *
 * Les deux ne se disent pas avec la même phrase, et l'écran ne doit pas laisser
 * croire que la salle a battu des séances du même atelier quand ce n'est pas le
 * cas. Sans `comparable`, ce bloc ne s'affiche pas du tout : mieux vaut ne rien
 * dire qu'annoncer « mieux que 100 % des parties » au premier atelier de l'année.
 *
 * `targetPerRound` part avec la requête : l'objectif d'une séance est un
 * réglage de CETTE page, que le serveur ne lit pas, et le repli compare des
 * parts d'objectif atteintes.
 */
const GameRanking = ({ gameId, targetPerRound }) => {
  const [ranking, setRanking] = useState(null);

  useEffect(() => {
    if (!gameId) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const query = targetPerRound ? `?target_per_round=${targetPerRound}` : '';
        const res = await fetch(`${process.env.REACT_APP_API_URL}/game_summary/${gameId}${query}`);
        if (!res.ok) return;
        const data = await res.json();
        if (!cancelled) setRanking(data?.percentile || null);
      } catch (error) {
        // Le bilan doit s'afficher même si cette comparaison échoue : elle est
        // un complément, pas le résultat de la séance.
        console.error('Classement de la partie indisponible:', error);
      }
    })();
    return () => { cancelled = true; };
  }, [gameId, targetPerRound]);

  if (!ranking?.comparable) return null;
  const onReference = ranking.basis === 'reference';
  return (
    <p className="board-note">
      Cette séance fait mieux que <strong>{ranking.percentile}%</strong> des{' '}
      <strong>{ranking.sample_size}</strong>{' '}
      {onReference ? 'séances de référence' : 'séances déjà jouées sur cet atelier'}.
      {onReference && (
        <span className="board-note-detail">
          Cet atelier n'a pas encore assez de séances pour se comparer à lui-même.
        </span>
      )}
    </p>
  );
};

const ACGEndPage = ({ pageData, gameStatus, onDecisionSelect,
                      displayButton = true, endsGame = false }) => {
  // Avant le repli de chargement : un hook ne se déclare pas derrière un
  // `return`. Il porte la page entière, dans laquelle le rapport va relever
  // les canvas des graphes.
  const boardRef = useRef(null);
  const scores = gameStatus?.score_dic;
  if (!scores) {
    return <div className="score-board-loading">Chargement du bilan…</div>;
  }

  const settings = pageData?.content?.metadata || {};
  const background = settings.background_image_url || DEFAULT_BACKGROUND;
  const metrics = (settings.selected_metrics || DEFAULT_METRICS).filter((id) => id in scores);

  const { hasLog, measures } = adoptedMeasures(gameStatus);
  const ledger = actorLedger(gameStatus, measures);
  const top = podium(ledger);
  const hardest = biggestEffort(ledger, hasLog);
  const byCategory = breakdownBy(measures, 'category', CATEGORY_LABELS);
  const sectors = sectorSavings(gameStatus);
  const { divisive, consensual } = measureConsensus(measures);
  const targetPerRound = targetPerRoundOf(gameStatus, settings.target_per_round);
  const progress = objectiveProgress(gameStatus, targetPerRound);
  const hasHistogram = Boolean(scores.water_projection?.metadata);
  const hasRadar = metrics.length >= 3;

  return (
    <div className="page-general-container">
      <div className="page-general-background-color"></div>

      <div className="page-general-background-hero" style={{ backgroundImage: `url(/img/bgrille-m.svg)` }}>
        <div className="page-general-background-image" style={{ backgroundImage: `url(${background})` }}></div>
      </div>

      <div className="page-main-content">
        <div className="board-page" ref={boardRef}>
          <header className="board-head">
            <h1 className="board-title">{pageData?.title || 'Le bilan de la séance'}</h1>
          </header>

          {/* LE BANDEAU DES CHIFFRES : ce que l'animateur annonce en premier,
              dans un seul bloc plutôt qu'éparpillé en bas de page. */}
          <section className="board-hero">
            <div className="board-hero-body">
              {progress && (
                <div className="acg-objective">
                  <div className="acg-objective-value">
                    {progress.percent}
                    <span className="acg-objective-unit">%</span>
                  </div>
                  <div className="acg-objective-side">
                    <p className="acg-objective-caption">de l'objectif d'économies d'eau</p>
                    <span className="acg-objective-track">
                      <span
                        className="acg-objective-fill"
                        style={{ width: percent(progress.share) }}
                      />
                    </span>
                    <p className="acg-objective-detail">
                      {progress.value} sur {progress.target} points visés
                      {gameStatus.nb_rounds_cg ? ` en ${gameStatus.nb_rounds_cg} tours` : ''}
                    </p>
                  </div>
                </div>
              )}

              <div className="board-figures">
                <div className="board-figure">
                  <div className="board-figure-value">{measures.length}</div>
                  <div className="board-figure-label">mesures adoptées</div>
                </div>
                {ledger.length > 0 && (
                  <div className="board-figure">
                    <div className="board-figure-value">{ledger.length}</div>
                    <div className="board-figure-label">acteurs</div>
                  </div>
                )}
                {gameStatus.nb_rounds_cg > 0 && (
                  <div className="board-figure">
                    <div className="board-figure-value">{gameStatus.nb_rounds_cg}</div>
                    <div className="board-figure-label">tours joués</div>
                  </div>
                )}
              </div>
            </div>

            <GameRanking gameId={gameStatus._id} targetPerRound={targetPerRound} />
          </section>

          {/* ── Le territoire ─────────────────────────────────────────
              Les indicateurs y sont : ils disent l'état du territoire à la
              fin de la séance, au même titre que la projection. */}
          <h2 className="board-section">Le territoire</h2>
          <div className="board-grid">
            {hasHistogram && (
              <Card
                wide
                chartKey="projection"
                title="Projection des économies d'eau"
                hint="Les économies cumulées tour après tour, empilées par secteur d'usage."
              >
                <WaterHistogramChart gameStatus={gameStatus} />
              </Card>
            )}

            {sectors.length > 0 && (
              <Card
                wide={!hasRadar}
                title="Économies d'eau par secteur"
                hint="D'où vient l'eau économisée, à l'horizon de la projection."
              >
                <div className="acg-sectors">
                  {sectors.map((row) => <SectorRow key={row.key} row={row} />)}
                </div>
              </Card>
            )}

            {hasRadar && (
              <Card
                chartKey="radar"
                title="Les indicateurs du territoire"
                hint="Où en sont les autres équilibres du territoire à la fin de la séance."
              >
                <RadarChart gameStatus={gameStatus} selectedMetrics={metrics} height="300px" width="100%" />
              </Card>
            )}
          </div>

          {/* ── Les leviers ───────────────────────────────────────────── */}
          <h2 className="board-section">Vos leviers</h2>
          <div className="board-grid">
            <Card
              title="Contraindre ou inciter"
              hint="La nature des mesures que la salle a fait passer."
            >
              {byCategory.length ? (
                <CategorySplit rows={byCategory} />
              ) : (
                <p className="board-empty">Aucune mesure adoptée.</p>
              )}
            </Card>

            <Card
              chartKey="levers"
              title="Les familles de leviers"
              hint="Le type d'action derrière chaque mesure adoptée."
            >
              <DecisionCategoryChart gameStatus={gameStatus} height="260px" width="100%" />
            </Card>

            {/* Sans journal des mesures, il n'y a ni titre ni position à
                montrer : le bloc s'efface au lieu d'afficher des identifiants. */}
            {hasLog && (divisive.length > 0 || consensual.length > 0) && (
              <Card
                wide
                title="Ce qui a fait débat"
                hint="Parmi les mesures adoptées, celles que les acteurs ont défendues ou combattues."
              >
                <div className="acg-debate">
                  <div className="acg-debate-col">
                    <h4 className="acg-column-title acg-column-title--against">Les plus clivantes</h4>
                    {divisive.length ? (
                      <ul className="acg-debate-list">
                        {divisive.map((row) => (
                          <DebateItem key={row.id} row={row} />
                        ))}
                      </ul>
                    ) : (
                      <p className="board-empty">Aucune mesure adoptée n'a divisé les acteurs.</p>
                    )}
                  </div>
                  <div className="acg-debate-col">
                    <h4 className="acg-column-title acg-column-title--for">Les plus consensuelles</h4>
                    {consensual.length ? (
                      <ul className="acg-debate-list">
                        {consensual.map((row) => (
                          <DebateItem key={row.id} row={row} />
                        ))}
                      </ul>
                    ) : (
                      <p className="board-empty">Aucune mesure n'a fait l'unanimité.</p>
                    )}
                  </div>
                </div>
              </Card>
            )}
          </div>

          {/* ── Les acteurs ───────────────────────────────────────────── */}
          <h2 className="board-section">Les acteurs</h2>
          <div className="board-grid">
            <Card title="Podium" hint="Les acteurs qui ont le mieux défendu leurs intérêts.">
              {top.length ? (
                <ol className="acg-podium">
                  {top.map((actor) => (
                    <li key={actor.id} className={`acg-podium-item acg-podium-item--${actor.rank}`}>
                      <span className="acg-medal" aria-hidden="true">{MEDALS[actor.rank - 1]}</span>
                      {actor.icon && <img src={actor.icon} alt="" className="acg-podium-icon" />}
                      <span className="acg-podium-name">{actor.name}</span>
                      <span className="acg-podium-value">{actor.value}</span>
                    </li>
                  ))}
                </ol>
              ) : (
                <p className="board-empty">Aucun acteur sélectionné pour cette séance.</p>
              )}
            </Card>

            <Card title="Le plus grand effort" hint="Celui qui a le plus cédé pendant la séance.">
              {hardest ? (
                <div className="acg-effort">
                  {hardest.icon && <img src={hardest.icon} alt="" className="acg-effort-icon" />}
                  <div>
                    <div className="acg-effort-name">{hardest.name}</div>
                    <p className="acg-effort-detail">
                      {hasLog && hardest.conceded > 0
                        ? `A laissé passer ${hardest.conceded} mesure${hardest.conceded > 1 ? 's' : ''} qu'il combattait.`
                        : `Satisfaction finale la plus basse (${hardest.value}).`}
                    </p>
                  </div>
                </div>
              ) : (
                <p className="board-empty">Indisponible.</p>
              )}
            </Card>

            {/* Le détail par acteur : le podium dit qui a gagné, ce tableau dit
                comment — obtenu ou cédé, ce que la seule satisfaction mélange. */}
            <Card wide title="Ce que chacun a obtenu ou cédé">
              <div className="acg-table-wrap">
                <table className="acg-table">
                  <thead>
                    <tr>
                      <th>Acteur</th>
                      <th>Satisfaction</th>
                      {hasLog && <th>Soutenues et adoptées</th>}
                      {hasLog && <th>Combattues et adoptées</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {ledger
                      .slice()
                      .sort((a, b) => b.value - a.value)
                      .map((actor) => {
                        const delta = actor.initial != null ? actor.value - actor.initial : null;
                        return (
                          <tr key={actor.id}>
                            <td className="acg-table-actor">
                              {actor.icon && <img src={actor.icon} alt="" className="acg-table-icon" />}
                              {actor.name}
                            </td>
                            <td>
                              <span className="acg-table-score">{actor.value}</span>
                              {delta != null && (
                                <span className={`acg-delta${delta < 0 ? ' acg-delta--down' : ''}`}>
                                  {delta >= 0 ? '+' : ''}{delta}
                                </span>
                              )}
                            </td>
                            {hasLog && (
                              <td className={actor.won ? 'acg-num--for' : 'acg-num--zero'}>{actor.won}</td>
                            )}
                            {hasLog && (
                              <td className={actor.conceded ? 'acg-num--against' : 'acg-num--zero'}>
                                {actor.conceded}
                              </td>
                            )}
                          </tr>
                        );
                      })}
                  </tbody>
                </table>
              </div>
            </Card>
          </div>

          <div className="board-actions">
            {/* Le rapport reste offert quand le bouton d'avancement disparaît :
                la partie finie, l'écran est toujours là sous la barre de fin,
                et c'est le moment où l'animateur va chercher son document. */}
            <ReportDownloadButton
              boardRef={boardRef}
              pageData={pageData}
              gameStatus={gameStatus}
              view={{
                measures, hasLog, ledger, top, hardest, byCategory, sectors,
                divisive, consensual, progress, metrics,
                targetPerRound,
              }}
            />
            <AdvanceButton
              pageData={pageData}
              onDecisionSelect={onDecisionSelect}
              displayButton={displayButton}
              endsGame={endsGame}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default ACGEndPage;
